import axios from "axios";
import Constants from "../constants/Constants";
import SharedPreferences from "../constants/SharedPreferences";
import {
  convertArrayBufferToBase64String,
  getFromStorage,
  logApiResponse,
  debug,
} from "../util/Utility";
import { checkAndRefreshOrgSettings, isOrgSettingsUpdateHeader } from "./api/internal/orgSettingsRefresh";
import { readMemberStatusHeader, refreshMemberStatusIfChanged } from "./api/internal/memberStatusRefresh";
import { getAccountKey, setAccountKey } from "../util/SessionRegistry";
import {
  isReauthenticationNeeded,
  isRefreshableAuthError,
  reportReauthenticationNeeded,
} from "../util/Reauthentication";
import { getActiveSessionId } from "../util/ActiveSession";

/**
 * Header naming which account inside a browser's session bundle a request is for. Must match
 * Constants.ACCOUNT_KEY_HEADER on the server.
 */
export const ACCOUNT_KEY_HEADER = "X-Bayun-Account";

/**
 * The one place an LMS client is created.
 *
 * Every factory goes through here so none can forget the account-key header, which no request can
 * do without. A factory that built its own axios instance did forget it once, and nine admin
 * endpoints answered 401 for a signed-in user.
 *
 * The header goes on in a request interceptor rather than the instance's headers, because a client
 * can outlive the moment its session learned its key.
 *
 * @param {Object} options
 * @param {Object} options.headers headers particular to this client
 * @param {String} options.sessionId the session this client acts for, when the caller knows it
 * @returns {AxiosInstance}
 */
function buildClient({ headers = {}, sessionId } = {}) {
  const axiosInstance = axios.create({
    baseURL: `${Constants.PROXY_URL}${Constants.BASE_URL}`,
    timeout: 100000000,
    headers: {
      "Content-Type": "application/json",
      "Accept-Language": "en-US,en;q=0.8",
      ...headers,
    },
    withCredentials: true,
  });

  axiosInstance.interceptors.request.use((config) => {
    const accountKey = accountKeyFor(sessionId);
    if (accountKey) {
      config.headers = config.headers || {};
      config.headers[ACCOUNT_KEY_HEADER] = accountKey;
    }
    return config;
  });

  attachAccountKeyCapture(axiosInstance, sessionId);
  return axiosInstance;
}

/**
 * The account key to send, falling back to whatever account this tab is acting as when the client
 * was not told a session.
 *
 * Only reads may fall back. Guessing wrong here costs a 401; guessing wrong while *storing* a key
 * would overwrite another account's, so captureAccountKey requires an explicit sessionId.
 */
function accountKeyFor(sessionId) {
  const resolved = sessionId || safeActiveSessionId();
  return resolved ? getAccountKey(resolved) : "";
}

function safeActiveSessionId() {
  try {
    return getActiveSessionId();
  } catch (error) {
    return "";
  }
}

/**
 * This method receives the appId and appKeyPairId and returns a non-auth client.
 * appKeyPairId may be a single id or comma-separated ids (for /memberInfo).
 *
 * @param {String} appId It is to be provided by the developer for all API calls to LMS.
 * @param {String} appKeyPairId App key pair id header value (single or comma-separated).
 * @param {String} sessionId the session being established or acted for.
 * @returns {AxiosInstance} The non-auth instance for getMemberInfo and authenticateMember API calls.
 */
export const getNonAuthRestClient = (appId, appKeyPairId, sessionId) =>
  buildClient({
    headers: { app_key_pair_id: appKeyPairId, application_id: appId },
    sessionId,
  });

/** Client for the /admin/** endpoints. Most of them resolve a session, so all of them need the key. */
export const getNonAuthRestClientForAdmin = (sessionId) => buildClient({ sessionId });

export async function getAuthRestClientForAdmin(sessionId) {
  const axiosInstance = buildClient({ sessionId });
  attachInterceptor(axiosInstance, sessionId);
  return axiosInstance;
}

export const getSetTwoFARestClient = async (sessionId) =>
  buildClient({
    headers: {
      application_id: await getFromStorage(sessionId, SharedPreferences.APP_ID),
    },
    sessionId,
  });

/**
 * Rest client for unauthenticated / app-scoped LMS calls (application_id only).
 * Raw app secret must never be sent to the server.
 *
 * @param {String} appId application id header
 * @param {String} sessionId the session being established or acted for, when there is one.
 * @returns {AxiosInstance}
 */
export const getTwoFARestClient = (appId, sessionId) =>
  buildClient({ headers: { application_id: appId }, sessionId });

/**
 * This method creates a restClient with an authToken for the API calls.
 * This method gets its data for headers from local storage.
 *
 * @param {String} sessionId id of the session in question.
 * @returns {AxiosInstance} The instance with authToken for the basic API calls.
 */
export async function getAuthRestclient(sessionId) {
  const axiosInstance = buildClient({
    headers: {
      application_id: await getFromStorage(sessionId, SharedPreferences.APP_ID),
    },
    sessionId,
  });

  attachInterceptor(axiosInstance, sessionId);

  return axiosInstance;
}

/**
 * Records the account key from responses on this client.
 *
 * The call that mints a session (/applicationKeyPairs) runs on an app-scoped client, since there is
 * no session to authenticate with yet — so the key can arrive on a client that is not the
 * authenticated one. Without capturing it there, every later request is unauthenticated.
 *
 * Needs an explicit sessionId; see accountKeyFor.
 *
 * @param {AxiosInstance} axiosInstance client to attach to
 * @param {String} sessionId session the key belongs to
 * @returns {AxiosInstance} the same instance, for chaining
 */
export function attachAccountKeyCapture(axiosInstance, sessionId) {
  if (!sessionId) {
    return axiosInstance;
  }
  axiosInstance.interceptors.response.use((response) => {
    captureAccountKey(sessionId, response?.headers);
    return response;
  });
  return axiosInstance;
}

/** Axios lower-cases response header names, but not every adapter does, so read both spellings. */
function captureAccountKey(sessionId, headers) {
  if (!sessionId || !headers) {
    return;
  }
  const accountKey =
    headers[ACCOUNT_KEY_HEADER.toLowerCase()] ?? headers[ACCOUNT_KEY_HEADER];
  if (accountKey) {
    setAccountKey(sessionId, accountKey);
  }
}

function attachInterceptor(axiosInstance, sessionId) {
  axiosInstance.interceptors.response.use(
    (response) => {
      const requestUrl = response?.config?.url ?? "";
      const settingsUpdate = response?.headers?.["x-settings-update"];

      if (
        sessionId &&
        isOrgSettingsUpdateHeader(settingsUpdate) &&
        !requestUrl.includes("/refreshOrgSettings")
      ) {
        checkAndRefreshOrgSettings(sessionId, axiosInstance);
      }

      // Skip the lockbox call itself: it is what the refresh performs, and reacting to its own
      // response would recurse.
      if (sessionId && !requestUrl.includes("/memberLockBox")) {
        // Deliberately not awaited, so it needs its own catch. A response that arrives after its
        // account logged out reads storage that has gone, and the rejection went nowhere: an
        // unhandled promise rejection, reported as a failed lockbox read during an account switch.
        refreshMemberStatusIfChanged(
          sessionId,
          readMemberStatusHeader(response?.headers),
        ).catch((error) => {
          console.log("Could not refresh member status:", error);
        });
      }

      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      console.log("error.config", error.config);
      
      // Which server responses mean what is decided in util/Reauthentication, so this client and
      // the host apps' own clients cannot drift apart on it.
      if (
        isRefreshableAuthError(error) &&
        !originalRequest?._retry &&
        !originalRequest.url.includes("/auth/refreshToken")
      ) {
        originalRequest._retry = true; // prevent infinite retry loop

        try {
          console.log("🔐 Refreshing token...");
          await axiosInstance.post("/auth/refreshToken");
          // Retry original request
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          console.error("❌ Token refresh failed:", refreshError);
          // The refresh credential is gone too, so nothing can revive this session.
          reportReauthenticationNeeded(sessionId || safeActiveSessionId());
          return Promise.reject(refreshError);
        }
      }

      if (isReauthenticationNeeded(error)) {
        reportReauthenticationNeeded(sessionId || safeActiveSessionId());
      }

      return Promise.reject(error);
    },
  );
}
